import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Lock, CheckCircle, XCircle, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const { language } = useLanguage();
  const { toast } = useToast();
  
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('form'); // 'form', 'success', 'error'
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMessage(language === 'es' 
        ? 'Enlace de restablecimiento inválido o faltante.' 
        : 'Invalid or missing reset link.');
    }
  }, [token, language]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast({
        title: language === 'es' ? 'Error' : 'Error',
        description: language === 'es' ? 'Las contraseñas no coinciden' : 'Passwords do not match',
        variant: "destructive"
      });
      return;
    }
    
    if (password.length < 6) {
      toast({
        title: language === 'es' ? 'Error' : 'Error',
        description: language === 'es' ? 'La contraseña debe tener al menos 6 caracteres' : 'Password must be at least 6 characters',
        variant: "destructive"
      });
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/auth/reset-password`, {
        token,
        new_password: password
      }, { withCredentials: true });
      
      setStatus('success');
      
      // Set user in context (they're now logged in)
      if (response.data.user) {
        setUser(response.data.user);
      }
      
      toast({
        title: language === 'es' ? '¡Contraseña actualizada!' : 'Password Updated!',
        description: response.data.message,
      });
      
      // Redirect to dashboard after 2 seconds
      setTimeout(() => {
        navigate('/members/dashboard', { replace: true });
      }, 2000);
      
    } catch (error) {
      setStatus('error');
      setErrorMessage(error.response?.data?.detail || 
        (language === 'es' 
          ? 'Error al restablecer la contraseña. Por favor intente de nuevo.' 
          : 'Failed to reset password. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center py-32 px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_75f4a617-7223-46ab-b5af-9fb0ce34d58c/artifacts/s7ixbz5d_customcolor_logo_transparent_background.png" 
            alt="Exciting.Travel" 
            className="h-20 w-auto mx-auto mb-6"
          />
        </div>

        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {status === 'form' && (
            <>
              <h1 className="text-2xl font-bold text-gray-900 mb-2 text-center">
                {language === 'es' ? 'Crear nueva contraseña' : 'Create New Password'}
              </h1>
              <p className="text-gray-600 text-center mb-6">
                {language === 'es' 
                  ? 'Ingresa tu nueva contraseña a continuación.' 
                  : 'Enter your new password below.'}
              </p>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="password" className="font-bold mb-2 flex items-center space-x-2">
                    <Lock size={16} className="text-[#84bc00]" />
                    <span>{language === 'es' ? 'Nueva contraseña' : 'New Password'}</span>
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="border-2 pr-10"
                      placeholder="••••••••"
                      minLength={6}
                      data-testid="reset-password-new"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {language === 'es' ? 'Mínimo 6 caracteres' : 'Minimum 6 characters'}
                  </p>
                </div>

                <div>
                  <Label htmlFor="confirmPassword" className="font-bold mb-2 flex items-center space-x-2">
                    <Lock size={16} className="text-[#84bc00]" />
                    <span>{language === 'es' ? 'Confirmar contraseña' : 'Confirm Password'}</span>
                  </Label>
                  <Input
                    id="confirmPassword"
                    type={showPassword ? "text" : "password"}
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="border-2"
                    placeholder="••••••••"
                    minLength={6}
                    data-testid="reset-password-confirm"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#84bc00] hover:bg-[#6a9600] text-black font-bold py-6 text-lg"
                  data-testid="reset-password-submit"
                >
                  {loading 
                    ? (language === 'es' ? 'Restableciendo...' : 'Resetting...') 
                    : (language === 'es' ? 'Restablecer contraseña' : 'Reset Password')}
                </Button>
              </form>
            </>
          )}

          {status === 'success' && (
            <div className="text-center">
              <CheckCircle className="w-16 h-16 text-[#84bc00] mx-auto mb-6" />
              <h1 className="text-2xl font-bold text-gray-900 mb-4">
                {language === 'es' ? '¡Contraseña actualizada!' : 'Password Updated!'}
              </h1>
              <p className="text-gray-600 mb-4">
                {language === 'es' 
                  ? 'Tu contraseña ha sido restablecida exitosamente.' 
                  : 'Your password has been reset successfully.'}
              </p>
              <p className="text-sm text-gray-500">
                {language === 'es' 
                  ? 'Redirigiendo al panel de miembros...' 
                  : 'Redirecting to members dashboard...'}
              </p>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center">
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
              <h1 className="text-2xl font-bold text-gray-900 mb-4">
                {language === 'es' ? 'Error de restablecimiento' : 'Reset Failed'}
              </h1>
              <p className="text-gray-600 mb-6">{errorMessage}</p>
              <Link to="/forgot-password">
                <Button className="bg-[#84bc00] hover:bg-[#6a9600] text-black font-bold">
                  {language === 'es' ? 'Solicitar nuevo enlace' : 'Request New Link'}
                </Button>
              </Link>
            </div>
          )}

          {status !== 'success' && (
            <div className="mt-6 pt-6 border-t text-center">
              <Link 
                to="/login" 
                className="text-[#84bc00] hover:text-[#6a9600] font-semibold inline-flex items-center"
              >
                <ArrowLeft size={16} className="mr-2" />
                {language === 'es' ? 'Volver al inicio de sesión' : 'Back to Login'}
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
